import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Grid, 
  Typography, Box, Chip
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { getIcon } from '../../../utils/IconHandler';
import { B2BClientService } from '../../../services/B2BServices';
import { formatMoney } from '../../../utils/currency';

const ViewB2BClientModal = ({ open, onClose, client, onSendPortalAccess, showPortal = false }) => {
  const { t } = useTranslation();
  const [portalStatus, setPortalStatus] = useState(null);
  const [loadingPortalStatus, setLoadingPortalStatus] = useState(false);

  useEffect(() => {
    if (!open || !client?.id || !showPortal) {
      setPortalStatus(null);
      return undefined;
    }

    let active = true;
    const loadPortalStatus = async () => {
      try {
        setLoadingPortalStatus(true);
        const status = await B2BClientService.getPortalAccountStatus(client.id);
        if (active) setPortalStatus(status);
      } catch (error) {
        console.error('Error loading B2B portal status:', error);
        if (active) setPortalStatus(null);
      } finally {
        if (active) setLoadingPortalStatus(false);
      }
    };

    loadPortalStatus();

    return () => {
      active = false;
    };
  }, [open, client?.id, showPortal]);

  if (!client) return null;

  const formatCurrency = (amount) => {
    return formatMoney(amount);
  };

  const getBalanceColor = (balance) => {
    if (balance > 0) return 'success';
    if (balance < 0) return 'error';
    return 'default';
  };

  const getBalanceText = (balance) => {
    if (balance > 0) return t('positive_balance');
    if (balance < 0) return t('negative_balance');
    return t('zero_balance');
  };

  const getPortalStatus = () => {
    if (portalStatus?.enabled && portalStatus?.hasPassword) {
      return { label: 'Activo', color: 'success' };
    }
    if (portalStatus?.invitationSentAt) {
      return { label: 'Invitación enviada', color: 'warning' };
    }
    return { label: 'Sin acceso', color: 'default' };
  };

  const handleSendPortalAccess = async () => {
    if (!onSendPortalAccess) return;
    await onSendPortalAccess(client);

    try {
      setLoadingPortalStatus(true);
      const status = await B2BClientService.getPortalAccountStatus(client.id);
      setPortalStatus(status);
    } catch (error) {
      console.error('Error refreshing B2B portal status:', error);
    } finally {
      setLoadingPortalStatus(false);
    }
  };

  const portalStatusView = getPortalStatus();
  const portalButtonText = portalStatus?.invitationSentAt || portalStatus?.enabled
    ? 'Reenviar acceso'
    : 'Enviar acceso';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {getIcon('eye')}
          {t('view_b2b_client')}
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Grid container spacing={3} sx={{ mt: 1 }}>
          {/* Datos Básicos */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom sx={{ borderBottom: 1, borderColor: 'divider', pb: 1 }}>
              {t('basic_data')}
            </Typography>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                {t('business_name')}
              </Typography>
              <Typography variant="body1">
                {client.razonSocial}
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12} md={4}>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                {t('fantasy_name')}
              </Typography>
              <Typography variant="body1">
                {client.nombreFantasia}
              </Typography>
            </Box>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                {t('rut')}
              </Typography>
              <Typography variant="body1">
                {client.rut}
              </Typography>
            </Box>
          </Grid>
          
          <Grid item xs={4}>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                {t('address')}
              </Typography>
              <Typography variant="body1">
                {client.direccionFiscal || t('not_specified')}
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12} md={8}>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                {t('email')}
              </Typography>
              <Typography variant="body1">
                {client.email || t('not_specified')}
              </Typography>
            </Box>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                {t('main_contact')}
              </Typography>
              <Typography variant="body1">
                {client.contacto}
              </Typography>
            </Box>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                {t('phone')}
              </Typography>
              <Typography variant="body1">
                {client.telefono || t('not_specified')}
              </Typography>
            </Box>
          </Grid>

          {showPortal && <>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ borderBottom: 1, borderColor: 'divider', pb: 1, mt: 2 }}>
                Portal B2B
              </Typography>
            </Grid>

            <Grid item xs={12} md={4}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Estado
                </Typography>
                <Chip
                  label={loadingPortalStatus ? 'Cargando...' : portalStatusView.label}
                  color={portalStatusView.color}
                  size="small"
                  sx={{ mt: 0.5 }}
                />
              </Box>
            </Grid>

            <Grid item xs={12} md={4}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Email de acceso
                </Typography>
                <Typography variant="body1">
                  {portalStatus?.email || client.email || t('not_specified')}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', height: '100%', alignItems: 'flex-end' }}>
                <Button
                  variant="outlined"
                  startIcon={getIcon('mail')}
                  onClick={handleSendPortalAccess}
                  disabled={!client.email || loadingPortalStatus}
                >
                  {portalButtonText}
                </Button>
              </Box>
            </Grid>

            {portalStatus?.invitationSentAt && (
            <Grid item xs={12} md={4}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Última invitación
                </Typography>
                <Typography variant="body1">
                  {portalStatus.invitationSentAt}
                </Typography>
              </Box>
            </Grid>
            )}

            {portalStatus?.activatedAt && (
            <Grid item xs={12} md={4}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Activado
                </Typography>
                <Typography variant="body1">
                  {portalStatus.activatedAt}
                </Typography>
              </Box>
            </Grid>
            )}

            {portalStatus?.lastLoginAt && (
            <Grid item xs={12} md={4}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Último ingreso
                </Typography>
                <Typography variant="body1">
                  {portalStatus.lastLoginAt}
                </Typography>
              </Box>
            </Grid>
            )}
          </>}

          {client.notes && (
            <Grid item xs={12}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  {t('notes')}
                </Typography>
                <Typography variant="body1">
                  {client.notes}
                </Typography>
              </Box>
            </Grid>
          )}
          
          <Grid item xs={12} md={4}>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                {t('account_balance')}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="h6" color={`${getBalanceColor(client.accountBalance)}.main`}>
                  {formatCurrency(client.accountBalance || 0)}
                </Typography>
                <Chip 
                  label={getBalanceText(client.accountBalance || 0)} 
                  color={getBalanceColor(client.accountBalance)} 
                  size="small" 
                />
              </Box>
            </Box>
          </Grid>

          {/* Acuerdo Comercial */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom sx={{ borderBottom: 1, borderColor: 'divider', pb: 1, mt: 2 }}>
              {t('commercial_agreement')}
            </Typography>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">
                {t('credit_days')}
              </Typography>
              <Typography variant="body1">
                {client.creditDays || 0} {t('days')}
              </Typography>
            </Box>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">
                {t('credit_limit')}
              </Typography>
              <Typography variant="body1">
                {formatCurrency(client.creditLimit || 0)}
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12} md={4}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Perfil comercial
              </Typography>
              <Typography variant="body1">
                {client.commercialProfileName || 'Sin perfil'}
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">
                {t('notes')}
              </Typography>
              <Typography variant="body1">
                {client.notes || '-'}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose}>
          {t('close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ViewB2BClientModal;
